import React from 'react';
import FieldOptions from '@components/FieldOptions/FieldOptions';
import { AllOptionsType, DateType, isDateType, MetricType, OptionalType } from '@components/FieldOptions/types';
import { iconStyles } from '@components/WithDescriptionTooltip/StyledTooltipTitleWithIcon';
import { Badge, badgeClasses, BadgeProps, svgIconClasses, TooltipProps } from '@mui/material';
import { FieldTypes } from '../../views/Reports/types';

export type StyledBadgeBaseProps = Omit<BadgeProps, 'badgeContent'> & {
  tooltipProps?: Omit<TooltipProps, 'children'>;
  persistent?: boolean;
  enabledFieldTypesWithOptions?: Set<string | undefined>;
};

export type StyledBadgeProps = StyledBadgeBaseProps & (AllOptionsType | OptionalType);

export const getAdditionalProps = (props: AllOptionsType | OptionalType) => {
  const { type = FieldTypes.Measurement, ...rest } = props;

  if (isDateType(type, props)) {
    return {
      type: type as DateType,
      ...rest,
      onApply: props.onApply,
      field: props.field,
      selectedFields: props.selectedFields,
    };
  }

  if (type === FieldTypes.Measurement) {
    return { type: type as MetricType, ...rest };
  }

  return { type: undefined };
};

export const StyledBadge = (props: StyledBadgeProps) => {
  const { current: id } = React.useRef(crypto.randomUUID());
  const { children, tooltipProps, persistent, enabledFieldTypesWithOptions, sx, ...rest } = props;

  return (
    <Badge
      sx={theme => ({
        [`& .${badgeClasses.badge}`]: {
          top: '50%',
          transform: 'scale(1) translate(0, -50%)',
          transformOrigin: '50% 0',
          cursor: 'pointer',
        },

        [`& .${svgIconClasses.root}`]: {
          opacity: persistent ? 1 : 0,
          transition: theme.transitions.create('opacity', { duration: theme.transitions.duration.shorter }),
          fontSize: 18,
        },

        [`& .${svgIconClasses.root} svg`]: iconStyles,
        [`:is([role=tooltip], &:hover) :is(& .${svgIconClasses.root}), 
          body:has([data-tooltip-id="${id}"]) & .${svgIconClasses.root}`]: {
          opacity: 1,
        },
        ...sx,
      })}
      badgeContent={
        <FieldOptions
          {...(props as any)}
          enabledFieldTypesWithOptions={enabledFieldTypesWithOptions}
          tooltipProps={tooltipProps && { ...tooltipProps, id }}
        />
      }
      {...rest}>
      {children}
    </Badge>
  );
};
